import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { AccessibleTenant } from '@cms/contracts'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { showTenantSwitcher } from '../domain/tenants.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { TextField } from '../ui/fields.js'
import { Dialog } from '../ui/overlays.js'
import { EmptyState, Icon, LinkButton, StatusChip, cx } from '../ui/primitives.js'

/**
 * The client picker, next to the site picker (document/61 M4). Shown only
 * when there is more than one client to see. Picking one is remembered in
 * this browser and every call from then on carries `X-Tenant`; clients an
 * operator sees without a membership are listed, disabled, with the way to
 * reach them (the operator panel).
 */
export function TenantSwitcher() {
  const { t } = useI18n()
  const { tenants, tenant } = useSiteWorkspace()
  const [open, setOpen] = useState(false)
  const location = useLocation()

  useEffect(() => setOpen(false), [location.pathname])
  if (!showTenantSwitcher(tenants)) return null

  return <>
    <button type="button" className="tenant-switcher" aria-haspopup="dialog" onClick={() => setOpen(true)} title={t('tenantSwitcher.button', { name: tenant?.name ?? '—' })} aria-label={t('tenantSwitcher.button', { name: tenant?.name ?? '—' })}>
      <Icon name="apartment" size={20}/>
      <span className="tenant-switcher-name">{tenant?.name ?? t('tenantSwitcher.none')}</span>
      <Icon name="expand_more" size={20}/>
    </button>
    {open && <TenantSwitcherDialog onClose={() => setOpen(false)}/>}
  </>
}

function TenantSwitcherDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()
  const { tenants, tenant, selectTenant, operator } = useSiteWorkspace()
  const [query, setQuery] = useState('')
  const visible = useMemo(() => {
    const wanted = query.trim().toLowerCase()
    return wanted ? tenants.filter((entry) => entry.name.toLowerCase().includes(wanted) || entry.id.toLowerCase().includes(wanted)) : tenants
  }, [tenants, query])
  const pick = (entry: AccessibleTenant) => {
    if (!entry.selectable) return
    selectTenant(entry.id)
    onClose()
  }

  return <Dialog
    title={t('tenantSwitcher.title')}
    description={t('tenantSwitcher.intro')}
    icon="apartment"
    size="md"
    onClose={onClose}
    className="switcher-dialog"
    actions={operator ? <LinkButton variant="tonal" icon="admin_panel_settings" to={paths.operator()} onClick={onClose}>{t('nav.operator')}</LinkButton> : undefined}
  >
    {tenants.length > 6 && <TextField label={t('tenantSwitcher.search')} hideLabel leadingIcon="search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('tenantSwitcher.search')} data-autofocus/>}
    <ul className="tenant-list">
      {visible.length === 0 ? <li><EmptyState compact icon="search" title={t('switcher.noMatch')}/></li>
        : visible.map((entry) => {
          const isCurrent = entry.id === tenant?.id
          return <li key={entry.id}>
            <button type="button" className={cx('tenant-option', isCurrent && 'is-current')} disabled={!entry.selectable} aria-current={isCurrent ? 'true' : undefined} onClick={() => pick(entry)}>
              <span className="list-row-icon"><Icon name={isCurrent ? 'check' : entry.selectable ? 'apartment' : 'lock'} size={20}/></span>
              <span className="list-row-text">
                <strong>{entry.name}</strong>
                <small>{entry.selectable
                  ? t('tenantSwitcher.counts', { sites: entry.siteCount, projects: entry.projectCount })
                  : t('tenantSwitcher.operatorOnly')}</small>
              </span>
              <StatusChip tone={entry.access === 'home' ? 'info' : entry.access === 'member' ? 'neutral' : 'warning'}>{t(`tenantSwitcher.access.${entry.access}`)}</StatusChip>
            </button>
          </li>
        })}
    </ul>
  </Dialog>
}
